import type Konva from "konva";

/* Types */
export type CanvasPoint = { x: number; y: number };

interface PointerPositionOptions {
  stage: Konva.Stage | null;
  scale: number;
  canvasSize: { width: number; height: number };
  ratio: number;
  requireInside?: boolean;
}

/* Helpers */
export const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, max));

/* Normalize two corner points into a top-left origin rect plus positive width/height */
export const normalizeRect = (start: CanvasPoint, end: CanvasPoint) => {
  // Use the smallest x/y as the rect origin so drag direction does not matter
  const x = Math.min(start.x, end.x);
  const y = Math.min(start.y, end.y);

  // Width and height should always be positive regardless of drag direction
  const width = Math.abs(end.x - start.x);
  const height = Math.abs(end.y - start.y);

  return { x, y, width, height };
};

/* Convert a rectangle into the 4-corner point format used by regions */
export const rectToPoints = (rect: { x: number; y: number; width: number; height: number }): CanvasPoint[] => [
  { x: rect.x, y: rect.y },
  { x: rect.x + rect.width, y: rect.y },
  { x: rect.x + rect.width, y: rect.y + rect.height },
  { x: rect.x, y: rect.y + rect.height },
];

/* Convert a 4-point rectangle region back into rect props for rendering/editing */
export const getRectFromPoints = (points: CanvasPoint[]) => ({
  x: points[0].x,
  y: points[0].y,
  width: points[2].x - points[0].x,
  height: points[2].y - points[0].y,
});

/* Clamp a rectangle so it cannot move beyond the canvas edges */
export const clampRectPosition = ({ x, y, width, height, canvasSize }: { x: number; y: number; width: number; height: number; canvasSize: { width: number; height: number } }) => ({
  x: clamp(x, 0, canvasSize.width - width),
  y: clamp(y, 0, canvasSize.height - height),
});

/* Check whether a set of points describes an axis-aligned rectangle */
export const isAxisAlignedRectangle = (points: CanvasPoint[]) => {
  // Rectangles in this editor are represented by exactly 4 points
  if (points.length !== 4) return false;

  // An axis-aligned rectangle only has 2 unique x values and 2 unique y values
  const xValues = Array.from(new Set(points.map((point) => point.x)));
  const yValues = Array.from(new Set(points.map((point) => point.y)));

  return xValues.length === 2 && yValues.length === 2;
};

/* Get the polygon bounds in canvas space for drag clamping */
export const getPolygonPointBounds = (polygonPoints: CanvasPoint[], ratio: number) => {
  // Scale points into canvas coordinates before measuring bounds
  const xValues = polygonPoints.map((point) => point.x * ratio);
  const yValues = polygonPoints.map((point) => point.y * ratio);

  return {
    minX: Math.min(...xValues),
    maxX: Math.max(...xValues),
    minY: Math.min(...yValues),
    maxY: Math.max(...yValues),
  };
};

/* Pick a stable label anchor near the top-left area of a polygon */
export const getPolygonLabelAnchor = (polygonPoints: CanvasPoint[]): CanvasPoint => {
  // Split the shape around its horizontal midpoint so labels prefer the left side
  const midX = polygonPoints.reduce((sum, point) => sum + point.x, 0) / polygonPoints.length;
  const leftPoints = polygonPoints.filter((point) => point.x <= midX);
  const candidatePoints = leftPoints.length > 0 ? leftPoints : polygonPoints;

  // From those candidates, choose the highest point and use leftmost as a tiebreaker
  return candidatePoints.reduce((best, point) => {
    if (point.y < best.y || (point.y === best.y && point.x < best.x)) return point;
    return best;
  }, candidatePoints[0]);
};

/* Convert the current Konva pointer position into image-space coordinates */
export const getPointerPositionInImage = ({ stage, scale, canvasSize, ratio, requireInside = false }: PointerPositionOptions): CanvasPoint | null => {
  // Bail out when the stage is unavailable or scaling information is invalid
  if (!stage || scale === 0 || ratio === 0) return null;

  // Konva tracks pointer position in stage space
  const pointer = stage.getPointerPosition();
  if (!pointer) return null;

  // Remove the stage pan offset and current zoom so we are back in image/canvas space
  const stagePos = stage.position();
  const unscaledX = (pointer.x - stagePos.x) / scale;
  const unscaledY = (pointer.y - stagePos.y) / scale;

  // Some callers only want positions that are strictly inside the image
  if (requireInside && (unscaledX < 0 || unscaledX > canvasSize.width || unscaledY < 0 || unscaledY > canvasSize.height)) {
    return null;
  }

  // Otherwise clamp to the image edge so drag/move interactions remain bounded
  const clampedX = clamp(unscaledX, 0, canvasSize.width);
  const clampedY = clamp(unscaledY, 0, canvasSize.height);

  // Convert back down to region-space coordinates before returning
  return {
    x: clampedX / ratio,
    y: clampedY / ratio,
  };
};
